import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* ======================= Helpers ======================= */

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

// ułamek zwykły / mieszany (bez kropki/przecinka)
const fractionToLatex = ({ numerator, denominator }) => {
  if (!denominator || denominator === 0) return "";
  if (denominator === 1) return `${numerator}`;
  return `\\dfrac{${numerator}}{${denominator}}`;
};

const simplifyFraction = (num, den) => {
  const gcd = (a, b) => {
    a = Math.abs(a);
    b = Math.abs(b);
    if (a === 0 && b === 0) return 1;
    while (b !== 0) {
      const t = b;
      b = a % b;
      a = t;
    }
    return a === 0 ? 1 : a;
  };

  const g = gcd(num, den);
  return { numerator: num / g, denominator: den / g };
};

const parseFractionFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // jeśli jest kropka/przecinek – traktujemy jako liczbę dziesiętną, nie ułamek
  if (trimmed.includes(".") || trimmed.includes(",")) return null;

  let sign = 1;
  let rest = trimmed;
  if (rest[0] === "-" || rest[0] === "+") {
    if (rest[0] === "-") sign = -1;
    rest = rest.slice(1).trim();
  }

  let whole = 0;
  let num;
  let den;

  const spaceParts = rest.split(/\s+/);

  if (spaceParts.length === 2) {
    // zapis mieszany: "2 3/5"
    whole = parseInt(spaceParts[0], 10);
    if (!Number.isFinite(whole)) return null;

    const fracParts = spaceParts[1].split("/");
    if (fracParts.length !== 2) return null;

    num = parseInt(fracParts[0], 10);
    den = parseInt(fracParts[1], 10);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;

    num = sign * (whole * den + num);
  } else {
    // "3/5" lub "4"
    const fracParts = rest.split("/");
    if (fracParts.length === 1) {
      num = sign * parseInt(fracParts[0], 10);
      den = 1;
      if (!Number.isFinite(num)) return null;
    } else if (fracParts.length === 2) {
      num = sign * parseInt(fracParts[0], 10);
      den = parseInt(fracParts[1], 10);
      if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    } else return null;
  }

  return simplifyFraction(num, den);
};

// liczba dziesiętna (kropka lub przecinek)
const parseDecimalFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;

  return value;
};

// format do max 2 miejsc po przecinku, bez nieznaczących zer
const formatMax2 = (value) => {
  let s = value.toFixed(2);
  s = s.replace(/\.?0+$/, "");
  return s;
};

// format do 4 miejsc po przecinku, bez końcowych zer
const formatRaw = (value) => {
  let s = value.toFixed(4);
  s = s.replace(/0+$/, "");
  if (s.endsWith(".")) s = s.slice(0, -1);
  return s;
};

/* ======================= GENERATOR ======================= */
/**
 * Zadanie: po dwóch zmianach ceny znamy cenę końcową
 * i mamy obliczyć cenę przed tymi zmianami.
 *
 * Cztery warianty zmian:
 *  ++   podwyżka, podwyżka
 *  --   obniżka,  obniżka
 *  +-   podwyżka, obniżka
 *  -+   obniżka,  podwyżka
 */
export function generateTask(pattern) {
  const VARS = ["++", "--", "+-", "-+"];
  if (!pattern || !VARS.includes(pattern)) {
    pattern = VARS[randInt(0, VARS.length - 1)];
  }

  const inc1 = pattern[0] === "+";
  const inc2 = pattern[1] === "+";

  const P = [5, 10, 15, 20, 25, 30, 35, 40];
  const p1 = P[randInt(0, P.length - 1)];
  const p2 = P[randInt(0, P.length - 1)];

  const a1 = 1 + (inc1 ? p1 : -p1) / 100;
  const a2 = 1 + (inc2 ? p2 : -p2) / 100;

  // dobieramy końcową cenę tak, aby cena początkowa była ładną liczbą
  const base = 10 * randInt(5, 25); // szukana cena
  const finalRaw = base * a1 * a2;
  const final = +finalRaw.toFixed(2); // cena po dwóch zmianach, zaokrąglona do groszy

  return {
    type: "PercentChangeReverseDoubleTask",
    data: {
      base,
      final,
      p1,
      p2,
      inc1,
      inc2,
      pattern,
      a1,
      a2,
    },
  };
}

/* ======================= RENDER TASK ======================= */

export function renderTask({ data }) {
  const { final, p1, p2, inc1, inc2 } = data;

  const v1 = inc1 ? "podniesiono" : "obniżono";
  const v2 = inc2 ? "podniesiono" : "obniżono";

  return (
    <p>
      Cenę pewnego towaru najpierw {v1} o <InlineMath math={`${p1}\\%`} />, a
      następnie {v2} o <InlineMath math={`${p2}\\%`} />. Po tych dwóch zmianach
      cena wynosi <InlineMath math={`${formatMax2(final)}\\,\\text{zł}`} />.
      Oblicz, ile ten towar kosztował przed zmianami.
    </p>
  );
}

/* ======================= INPUT ======================= */

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder="np. 150, 150.5 albo 150 1/2"
      label=""
      suffixLatex="\text{zł}"
    />
  );
}

/* ======================= CHECK ======================= */

export function checkAnswer({ data }, userAnswer) {
  const expected = data.base;

  if (!userAnswer || !userAnswer.raw) {
    return { isCorrect: false, expected, actual: null };
  }

  const raw = String(userAnswer.raw).trim();

  // 1) liczba dziesiętna
  let val = parseDecimalFromString(raw);

  // 2) jeśli się nie udało – ułamek zwykły / mieszany
  if (val === null) {
    const frac = parseFractionFromString(raw);
    if (frac) {
      val = frac.numerator / frac.denominator;
    }
  }

  if (val === null) {
    return { isCorrect: false, expected, actual: null };
  }

  const ok = Math.abs(val - expected) <= 0.01; // tolerancja 1 grosz

  return { isCorrect: ok, expected, actual: val };
}

/* ======================= ANSWER ======================= */

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.base}\\,\\text{zł}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  const norm = userAnswer.raw.replace(",", ".");
  return <InlineMath math={`${norm}\\,\\text{zł}`} />;
}

/* ======================= EXPLANATION ======================= */

export function renderExplanation({ data }) {
  const { final, p1, p2, inc1, inc2, a1, a2, base } = data;

  const s1 = inc1 ? "+" : "-";
  const s2 = inc2 ? "+" : "-";

  const a1Str = formatMax2(a1);
  const a2Str = formatMax2(a2);
  const finalStr = formatMax2(final);

  const denom = a1 * a2;
  const denomStr = formatRaw(denom);

  const step1a = `a_1 = 1 ${s1} \\dfrac{${p1}}{100} = ${a1Str}`;
  const step1b = `a_2 = 1 ${s2} \\dfrac{${p2}}{100} = ${a2Str}`;

  const step2 = `K = x \\cdot a_1 \\cdot a_2`;

  const step3 = `x = \\dfrac{${finalStr}}{${a1Str}\\cdot ${a2Str}} = \\dfrac{${finalStr}}{${denomStr}} \\approx ${base}\\,\\text{zł}`;

  return (
    <div className="space-y-2" style={{ marginTop: 12 }}>
      <p>
        <strong>Krok 1:</strong> Zapisz współczynniki zmian ceny:
      </p>

      <BlockMath math={step1a} />
      <BlockMath math={step1b} />

      <p>
        <strong>Krok 2:</strong> Po dwóch zmianach cena spełnia równanie:
      </p>

      <BlockMath math={step2} />

      <p>
        <strong>Krok 3:</strong> Podstaw dane i oblicz cenę początkową:
      </p>

      <BlockMath math={step3} />
    </div>
  );
}
